import { create } from 'zustand/react';
import type { TPreferencesType } from '@/stores/preferences/types';
import { createPickHook } from '../util';
import { getDefaultPreferences } from '@/config/preferences';
import {
  getPreferencesFromCookie,
  savePreferencesToCookie,
  resetPreferences as resetPreferencesToCookie,
} from '@/lib/utils/preferences';

// Always use DB for preferences (with cookie as fallback/cache)
const USE_DB_PREFERENCES = typeof window !== 'undefined';

const initialState = {
  preferences: getDefaultPreferences(),
  isLoaded: false,
  userId: undefined as string | undefined,
};

/**
 * Fetches preferences from the API and returns flat format.
 */
async function fetchPreferencesFromAPI(
  userId: string,
): Promise<Record<string, boolean | string> | null> {
  try {
    const response = await fetch(`/my-account/api/preferences?flat=true`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.preferences || null;
  } catch (error) {
    console.error('Error fetching preferences from API:', error);
    return null;
  }
}

/**
 * Saves preferences to the API (PATCH).
 */
async function savePreferencesToAPI(
  patch: Record<string, boolean | string>,
): Promise<boolean> {
  try {
    const response = await fetch(`/my-account/api/preferences?flat=true`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patch),
    });

    return response.ok;
  } catch (error) {
    console.error('Error saving preferences to API:', error);
    return false;
  }
}

/**
 * Resets preferences via API (DELETE).
 */
async function resetPreferencesViaAPI(): Promise<boolean> {
  try {
    const response = await fetch(`/my-account/api/preferences`, {
      method: 'DELETE',
      credentials: 'include',
    });

    return response.ok;
  } catch (error) {
    console.error('Error resetting preferences via API:', error);
    return false;
  }
}

/**
 * Migrates cookie preferences to DB.
 */
async function migratePreferencesToDB(
  cookiePreferences: Record<string, boolean | string>,
): Promise<boolean> {
  try {
    const response = await fetch(`/my-account/api/preferences`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cookieData: cookiePreferences,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error migrating preferences to DB:', error);
    return false;
  }
}

export const PreferencesStore = create<TPreferencesType>((set, get) => ({
  ...initialState,

  setUserId: (userId: string | undefined) => {
    set({ userId });

    if (!userId) {
      // No user, load from anonymous cookie
      const preferences = getPreferencesFromCookie(undefined);
      set({ preferences, isLoaded: true });
      if (typeof window !== 'undefined') {
        window.pierrePreferences = preferences;
      }
      return;
    }

    // Load preferences with dual-read strategy
    const cookiePreferences = getPreferencesFromCookie(userId);

    if (USE_DB_PREFERENCES) {
      // Try to load from DB, fallback to cookie
      fetchPreferencesFromAPI(userId).then((dbPreferences) => {
        if (dbPreferences) {
          set({ preferences: dbPreferences, isLoaded: true });
          if (typeof window !== 'undefined') {
            window.pierrePreferences = dbPreferences;
          }

          // Sync cookie with DB (for offline/fast access)
          savePreferencesToCookie(dbPreferences, userId);
        } else {
          // DB fetch failed, use cookie and try to migrate
          set({ preferences: cookiePreferences, isLoaded: true });
          if (typeof window !== 'undefined') {
            window.pierrePreferences = cookiePreferences;
          }

          // Attempt migration in background
          migratePreferencesToDB(cookiePreferences);
        }
      });

      // Optimistically set cookie preferences while DB loads
      set({ preferences: cookiePreferences, isLoaded: true });
      if (typeof window !== 'undefined') {
        window.pierrePreferences = cookiePreferences;
      }
    } else {
      // DB feature disabled, use cookie only
      set({ preferences: cookiePreferences, isLoaded: true });
      if (typeof window !== 'undefined') {
        window.pierrePreferences = cookiePreferences;
      }
    }
  },

  setPreference: (key: string, value: boolean | string) => {
    const { preferences: currentPreferences, userId } = get();
    const updatedPreferences = {
      ...currentPreferences,
      [key]: value,
    };

    // Always save to cookie (fast, offline-first)
    savePreferencesToCookie(updatedPreferences, userId);
    set({ preferences: updatedPreferences });

    if (typeof window !== 'undefined') {
      window.pierrePreferences = updatedPreferences;
    }

    // Dual-write to DB if enabled
    if (USE_DB_PREFERENCES && userId) {
      savePreferencesToAPI({ [key]: value });
    }
  },

  setPreferences: (preferences) => {
    const { userId } = get();

    // Always save to cookie
    savePreferencesToCookie(preferences, userId);
    set({ preferences });

    if (typeof window !== 'undefined') {
      window.pierrePreferences = preferences;
    }

    // Dual-write to DB if enabled
    if (USE_DB_PREFERENCES && userId) {
      savePreferencesToAPI(preferences);
    }
  },

  resetPreferences: () => {
    const { userId } = get();

    // Reset cookie
    const defaults = resetPreferencesToCookie(userId);
    set({ preferences: defaults });

    if (typeof window !== 'undefined') {
      window.pierrePreferences = defaults;
    }

    // Reset DB if enabled
    if (USE_DB_PREFERENCES && userId) {
      resetPreferencesViaAPI();
    }
  },

  loadFromCookie: (userId?: string) => {
    if (userId) {
      set({ userId });
    }

    const currentUserId = userId || get().userId;
    const cookiePreferences = getPreferencesFromCookie(currentUserId);

    if (USE_DB_PREFERENCES && currentUserId) {
      // Try to load from DB
      fetchPreferencesFromAPI(currentUserId).then((dbPreferences) => {
        if (dbPreferences) {
          set({ preferences: dbPreferences, isLoaded: true });
          if (typeof window !== 'undefined') {
            window.pierrePreferences = dbPreferences;
          }

          // Sync cookie with DB
          savePreferencesToCookie(dbPreferences, currentUserId);
        } else {
          // DB fetch failed, use cookie
          set({ preferences: cookiePreferences, isLoaded: true });
          if (typeof window !== 'undefined') {
            window.pierrePreferences = cookiePreferences;
          }
        }
      });

      // Optimistically set cookie preferences
      set({ preferences: cookiePreferences, isLoaded: true });
      if (typeof window !== 'undefined') {
        window.pierrePreferences = cookiePreferences;
      }
    } else {
      // DB disabled or no user, use cookie only
      set({ preferences: cookiePreferences, isLoaded: true });
      if (typeof window !== 'undefined') {
        window.pierrePreferences = cookiePreferences;
      }
    }
  },
}));

// Expose store globally on window
if (typeof window !== 'undefined') {
  window.pierrePreferencesStore = PreferencesStore;
}

export const usePreferencesStore =
  createPickHook<TPreferencesType>(PreferencesStore);

export default usePreferencesStore;
